import { useState, useRef } from 'react';
import { useZipLookup } from '../../hooks/useZipLookup';
import type { QuestionDefinition } from '../../types/onboarding';
import { AlertExclamationIcon } from '../icons';

const cx = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

const INPUT_BASE_CLASSES =
  'w-full rounded-xl border border-onboarding-primary/30 bg-onboarding-content-bg px-5 py-[18px] text-base text-onboarding-text-dark transition focus:border-onboarding-primary focus:outline-none';

type ZipCodeFieldProps = {
  question: QuestionDefinition;
  value: string;
  onChange: (value: string) => void;
  hasError?: boolean;
};

export function ZipCodeField({ question, value, onChange, hasError }: ZipCodeFieldProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const { suggestions, resolvedLocation } = useZipLookup(value);

  const showDropdown = isFocused && suggestions.length > 0;

  const selectSuggestion = (zip: string) => {
    onChange(zip);
    setHighlightIndex(-1);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(i => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(i => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[highlightIndex].postal_code);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsFocused(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          id={question.id}
          className={cx(
            INPUT_BASE_CLASSES,
            "font-['Helvetica Neue','Arial',sans-serif]",
            'placeholder:text-[#707C8B]',
            hasError && 'border-red-600 pr-12',
            resolvedLocation && !hasError && 'pr-40'
          )}
          type="text"
          inputMode="numeric"
          maxLength={5}
          value={value}
          onChange={e => {
            const digits = e.target.value.replace(/\D/g, '');
            onChange(digits);
            setHighlightIndex(-1);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          placeholder={question.label}
          autoComplete="off"
          role="combobox"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          aria-controls={showDropdown ? 'zip-suggestions' : undefined}
          aria-activedescendant={
            showDropdown && highlightIndex >= 0
              ? `zip-option-${highlightIndex}`
              : undefined
          }
        />
        {resolvedLocation && !hasError && (
          <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-sm text-[#707C8B]">
            {resolvedLocation}
          </span>
        )}
        {hasError && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
            <AlertExclamationIcon />
          </div>
        )}
      </div>

      {showDropdown && (
        <ul
          id="zip-suggestions"
          role="listbox"
          className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-onboarding-primary/20 bg-onboarding-content-bg shadow-lg"
        >
          {suggestions.map((s, i) => (
            <li
              key={s.postal_code}
              id={`zip-option-${i}`}
              role="option"
              aria-selected={i === highlightIndex}
              className={cx(
                'cursor-pointer px-5 py-3 text-base transition',
                i === highlightIndex
                  ? 'bg-onboarding-primary/10 text-onboarding-text-dark'
                  : 'text-onboarding-text-dark hover:bg-onboarding-primary/5'
              )}
              onMouseDown={e => {
                e.preventDefault();
                selectSuggestion(s.postal_code);
              }}
            >
              <span className="font-medium">{s.postal_code}</span>
              <span className="ml-2 text-sm text-[#707C8B]">
                {s.city}, {s.state}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

